import React from 'react';
import { MessageSquare, Phone, Loader2 } from 'lucide-react';
import { useWhatsAppConnections } from '../../../hooks/useWhatsAppConnections';
import { Card } from '../../../../../components/ui/card';

interface AgentSelectionViewProps {
  onSelectAgent: (agentId: string) => void;
  /**
   * Optional pre-selected connection identifier. Can be either the connection.id
   * or the phone number (numero_telefono). If provided, the component will
   * attempt to auto-invoke `onSelectAgent` when connections finish loading.
   */
  selectedAgentId?: string;
}

export const AgentSelectionView: React.FC<AgentSelectionViewProps> = ({ onSelectAgent, selectedAgentId }) => {
  const { connections, loading, error } = useWhatsAppConnections();
  const hasAutoSelectedRef = React.useRef(false);

  // Auto-select agent if selectedAgentId is provided
  React.useEffect(() => {
    if (hasAutoSelectedRef.current) return;
    if (loading) return;
    if (!selectedAgentId) return;
    if (!connections || connections.length === 0) return;

    const byId = connections.find(c => c.id === selectedAgentId);
    const byPhone = connections.find(c => c.numero_telefono === selectedAgentId);
    const match = byId || byPhone;
    if (match) {
      hasAutoSelectedRef.current = true;
      onSelectAgent(match.id);
    }
  }, [connections, loading, selectedAgentId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Cargando agentes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center space-y-4">
          <MessageSquare className="h-16 w-16 text-destructive/50 mx-auto" />
          <div>
            <p className="text-lg font-medium text-destructive mb-2">
              Error al cargar agentes
            </p>
            <p className="text-sm text-muted-foreground">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center space-y-4">
          <MessageSquare className="h-16 w-16 text-muted-foreground/50 mx-auto" />
          <div>
            <p className="text-lg font-medium text-foreground mb-2">
              No hay agentes WhatsApp disponibles
            </p>
            <p className="text-sm text-muted-foreground">
              Primero debes conectar un agente WhatsApp en el módulo de Canales
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-full p-8">
      <div className="w-full max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {connections.map((connection) => (
            <Card
              key={connection.id}
              className="p-6 cursor-pointer hover:border-primary hover:shadow-lg transition-all duration-200 group"
              onClick={() => onSelectAgent(connection.id)}
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <MessageSquare className="h-8 w-8 text-primary" />
                </div>

                <div className="space-y-2">
                  <h3 className="font-semibold text-lg text-foreground group-hover:text-primary transition-colors">
                    {connection.nombre_instancia}
                  </h3>

                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span className="text-sm">{connection.numero_telefono}</span>
                  </div>

                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 text-green-700 text-xs font-medium">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    Conectado
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
